import { Injectable, Signal, computed } from '@angular/core';
import { IButtonState, IInfoButtonState } from '../../types/buttons/button-state.interface';
import { CPOs, OCPPComunication, State } from 'common_library';
import { MainStateService } from './app-main-state.service';

export interface ISendingCmds {
  state: boolean;
  connectorId: number;
}

/**
 * Stato dei bottoni pilotato dalla SESSIONE dell'utente (RFC stato-colonnine): l'azione dipende da
 * cosa può fare l'utente sulla SUA sessione, non dallo stato fisico del connettore. Il badge del
 * connettore (fusione fisico⊕sessione) lo calcola il backend; qui si guarda solo `mineSession`.
 *   nessuna → Avvia · RESERVED → Cancella/Avvia · CHARGING → Stop · OCCUPIED → (fine, gestito sopra il blocco)
 */
@Injectable({
  providedIn: 'root',
})

export class ButtonStateService {
  // La MIA sessione sul connettore selezionato (undefined = nessuna sessione mia).
  private $mineSession = computed(() => this.mainStateService.$selectedConnectorMineSession());

  $stationOnline = computed(() => this.mainStateService.$selectedStation()?.online);
  $baseCondition = computed(() => {
    return this.$stationOnline() && !this.mainStateService.$connectorCommandInProgress()
  });

  // CANCELLA PRENOTAZIONE: ho una mia sessione in stato prenotato.
  $buttonCancelReservation: Signal<IButtonState> = computed(() => {
    if (this.$baseCondition() && this.$mineSession()?.state === State.RESERVED) {
      return { show: true, disabled: false }
    }
    return { show: false, disabled: false }
  });

  // AVVIA PRENOTAZIONE: connettore libero e nessuna mia sessione (esclusi CPO roaming che non la supportano).
  $buttonStartReservation: Signal<IButtonState> = computed(() => {
    if (
      this.mainStateService.$selectedStation().cpo === CPOs.FREETOX
      || this.mainStateService.$selectedStation().cpo === CPOs.ENELX
    ) {
      return { show: true, disabled: true }
    }
    const conn = this.mainStateService.$selectedConnector();
    // Non abilitare la prenotazione se qualcuno sta già attaccando il cavo: il fisico `innerState`
    // è Preparing anche quando il numerico è ancora AVAILABLE (ripristino guard tolto nel refactor).
    if (
      this.$baseCondition()
      && this.$mineSession() === undefined
      && conn?.state === State.AVAILABLE
      && conn?.innerState !== OCPPComunication.InnerState.Preparing
    ) {
      return { show: true, disabled: false }
    }
    return { show: true, disabled: true }
  });

  // STOP: la MIA sessione è in erogazione. Gate su CHARGING + guardia teardown: `sessionStateToLegacy`
  // NON scrive il numerico per ENDING/ENDED → `state` resta congelato a 3 (CHARGING) durante la chiusura.
  // Senza `!stoppedAt && !endedAt` lo Stop ricomparirebbe in ENDING (rischio doppio-Stop / double-charge).
  $buttonStopCharge: Signal<IButtonState> = computed(() => {
    const s = this.$mineSession();
    const charging = s?.state === State.CHARGING && !s?.stoppedAt && !s?.endedAt;
    if (this.$baseCondition() && charging) {
      return { show: true, disabled: false }
    }
    return { show: false, disabled: false }
  });

  // AVVIA RICARICA: sempre visibile (default). Abilitato quando: prenotazione mia da avviare, oppure
  // nessuna sessione mia e connettore libero/in preparazione.
  $buttonStartCharge: Signal<IButtonState> = computed(() => {
    const s = this.$mineSession()?.state;
    const connState = this.mainStateService.$selectedConnector()?.state;
    const canStartReserved = s === State.RESERVED;
    const canStartFresh = s === undefined && (connState === State.AVAILABLE || connState === State.PREPARING);
    return {
      show: true,
      disabled: !(this.$baseCondition() && (canStartReserved || canStartFresh)),
    }
  });

  // INFO: stati non-azionabili (avvio / autorizzazione pagamento) → nessuna azione, solo spiegazione.
  $buttonInfo: Signal<IInfoButtonState> = computed(() => {
    const s = this.$mineSession()?.state;
    const reason = s === State.PENDING_AUTH ? 'auth' : s === State.PENDING ? 'starting' : undefined;
    return { show: !!reason, disabled: false, reason };
  });

  constructor(
    private mainStateService: MainStateService) { }
}
